/**
 * Manual cache pin/unpin for the Flow Designer's "Pin cache" toolbar action.
 * See `docs/features/cache-pinning.md` § Manual pin, org setting, CLI.
 *
 * Pinning is idempotent server-side (`POST` / `DELETE /workflows/:id/
 * cache-pin`) — this hook only owns kicking the mutation off and
 * invalidating the workflow detail query, which carries the resulting
 * `cachePinnedAt` / `effectivePinned` pair.
 *
 * @module ui/pages/FlowDesigner/hooks/useCachePin
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

/** Pins (`pin: true`) or unpins (`pin: false`) the workflow's cache. */
export function useCachePinMutation(workflowId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pin: boolean): Promise<void> => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/cache-pin`, {
        method: pin ? 'POST' : 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, pin ? 'Failed to pin cache' : 'Failed to unpin cache');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}
